import "./cleaner.css";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Banner } from "../components/ui";
import { fmtDuration, fmtTime } from "../lib/format";
import { type FlushOutcome, flushQueue, type PunchKind, type QueuedPunch } from "../lib/offlineQueue";
import { isClockToken } from "../lib/scanner";
import { useSession } from "../session";
import { useFlushNotes, useNow, useOnline, usePunchQueue } from "./hooks";
import { type FlowMode, PunchFlow } from "./PunchFlow";
import { FridayStockPrompt } from "./StockPrompt";
import { firstName } from "./wording";

interface OpenFlow {
  kind: PunchKind;
  mode: FlowMode;
  token?: string;
}

/** Router state set by ScannedLinkPage when a sticker was opened with the phone's camera. */
interface ScannedState {
  scannedToken?: unknown;
}

function describeFlush(outcomes: FlushOutcome[]): { kind: "ok" | "warn"; lines: string[] } {
  const lines = outcomes.map((o) => {
    const what = o.item.kind === "in" ? "clock-in" : "clock-out";
    const at = fmtTime(o.item.req.capturedAt ?? o.item.queuedAt);
    const r = o.response;
    if (r?.status === "accepted" || r?.status === "pending") {
      const done = `${o.item.kind === "in" ? "Clocked in" : "Clocked out"} at ${r.visit.siteName}`;
      return `Your saved ${what} from ${at} was sent. ${done}${r.status === "pending" ? " — waiting for approval" : ""}.`;
    }
    if (r) return `Your saved ${what} from ${at} couldn't be used: ${r.message}`;
    return `Your saved ${what} from ${at} couldn't be used: ${o.error?.message ?? "unknown problem"}`;
  });
  const bad = outcomes.some(
    (o) => o.error || o.response?.status === "rejected" || o.response?.status === "blocked",
  );
  return { kind: bad ? "warn" : "ok", lines };
}

function othersSummary(others: QueuedPunch[]): string {
  const n = others.length;
  const what = others.every((o) => o.kind === "in")
    ? "clock-in"
    : others.every((o) => o.kind === "out")
      ? "clock-out"
      : "clock-in/out";
  return `${n} saved ${what}${n === 1 ? "" : "s"} on this phone ${n === 1 ? "belongs" : "belong"} to another account — sign in as them to send ${n === 1 ? "it" : "them"}.`;
}

function queueSummary(queue: QueuedPunch[]): string {
  const first = queue[0];
  if (queue.length === 1 && first) {
    const what = first.kind === "in" ? "clock-in" : "clock-out";
    return `Your ${what} from ${fmtTime(first.req.capturedAt ?? first.queuedAt)} is saved on this phone.`;
  }
  return `${queue.length} clock-ins and clock-outs are saved on this phone.`;
}

/** Friday stock pop-up state for this app session (resets when the app is reopened). */
const stockPrompt = { snoozed: false, justPunched: false };

export function ClockPage() {
  const { me, reload } = useSession();
  const { mine: queue, others } = usePunchQueue();
  const { notes, dismiss } = useFlushNotes();
  const online = useOnline();
  const now = useNow(15_000);
  const location = useLocation();
  const navigate = useNavigate();
  const [flow, setFlow] = useState<OpenFlow | null>(null);
  const [sendingQueue, setSendingQueue] = useState(false);
  // Friday stock check: "Ask me later" hides the pop-up until the next clock-in or out.
  // Kept outside the component so it survives visiting another tab and coming back.
  const [stockSnoozed, setStockSnoozedState] = useState(() => stockPrompt.snoozed);
  const [justPunched, setJustPunchedState] = useState(() => stockPrompt.justPunched);
  const setStockSnoozed = (v: boolean) => {
    stockPrompt.snoozed = v;
    setStockSnoozedState(v);
  };
  const setJustPunched = (v: boolean) => {
    stockPrompt.justPunched = v;
    setJustPunchedState(v);
  };
  const flushNote = notes.length ? describeFlush(notes) : null;

  // What to show while punches are waiting on this phone: the newest queued punch wins.
  const lastQueued = queue.at(-1);
  const open = me?.openVisit ?? null;
  const clockedIn = lastQueued ? lastQueued.kind === "in" : !!open;
  const queuedIn = lastQueued?.kind === "in" ? lastQueued : null;
  const since = queuedIn ? (queuedIn.req.capturedAt ?? queuedIn.queuedAt) : (open?.in.at ?? null);
  const siteName = queuedIn ? queuedIn.siteName : (open?.siteName ?? null);
  const nextKind: PunchKind = clockedIn ? "out" : "in";

  // A sticker opened from the phone's camera (via /c/<token>): clock with it, no camera needed.
  const handledState = useRef<unknown>(null);
  useEffect(() => {
    const token = (location.state as ScannedState | null)?.scannedToken;
    if (typeof token !== "string" || !isClockToken(token) || handledState.current === location.state) return;
    handledState.current = location.state;
    setFlow({ kind: nextKind, mode: "preset", token });
    // Clear the state so a refresh doesn't clock again.
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate, nextKind]);

  if (!me) return null;

  const elapsedMin = since != null ? Math.max(0, (now - since) / 60_000) : null;
  const waitingApproval = !queuedIn && open?.in.result === "pending";

  return (
    <div className="page-narrow cl-page">
      <p className="cl-hello">Hi {firstName(me.user.name)}</p>

      {flushNote && (
        <Banner kind={flushNote.kind}>
          <div className="row between cl-nowrap">
            <div>
              {flushNote.lines.map((l) => (
                <p key={l} className="cl-tight">
                  {l}
                </p>
              ))}
            </div>
            <button className="btn ghost sm" onClick={() => dismiss()} aria-label="Dismiss">
              ✕
            </button>
          </div>
        </Banner>
      )}

      {!online && (
        <Banner kind="info">
          You're offline. You can still clock in and out — it will send when you have signal.
        </Banner>
      )}

      <section className={`cl-status ${clockedIn ? "on" : "off"}`} aria-live="polite">
        <div className="cl-status-label">
          <span className="cl-dot" aria-hidden="true" />
          {clockedIn ? "Clocked in" : "Not clocked in"}
        </div>
        {clockedIn ? (
          <>
            <div className="cl-site-name">
              {siteName ?? (queuedIn ? "The site on the sticker you scanned" : "Your site")}
            </div>
            {since != null && <div className="cl-since">since {fmtTime(since)}</div>}
            {elapsedMin != null && <div className="cl-elapsed">{fmtDuration(elapsedMin)}</div>}
            {waitingApproval && <span className="badge info">Clock-in waiting for approval</span>}
            {queuedIn && <span className="badge warn">Saved on this phone — not sent yet</span>}
          </>
        ) : (
          <p className="cl-status-help">
            Tap Clock in when you arrive, then scan the code sticker at the site.
          </p>
        )}
      </section>

      <button
        className={`btn huge cl-main ${clockedIn ? "cl-main-out" : "primary"}`}
        onClick={() => setFlow({ kind: nextKind, mode: "scan" })}
      >
        {clockedIn ? "Clock out" : "Clock in"}
      </button>
      <button className="btn ghost block" onClick={() => setFlow({ kind: nextKind, mode: "manual" })}>
        Can't scan the code?
      </button>

      {queue.length > 0 && (
        <div className="banner warn cl-queue">
          <p className="cl-tight">
            <strong>{queueSummary(queue)}</strong> {queue.length === 1 ? "It" : "They"} will be sent
            automatically when you have signal.
          </p>
          {queue[0]?.lastError && (
            <p className="cl-tight small">
              The last try didn't go through: {queue[0].lastError.message} If this keeps happening, please
              tell the office.
            </p>
          )}
          <button
            className="btn sm"
            disabled={sendingQueue || !online}
            onClick={async () => {
              setSendingQueue(true);
              try {
                await flushQueue();
              } finally {
                setSendingQueue(false);
              }
            }}
          >
            {sendingQueue ? "Sending…" : "Send now"}
          </button>
        </div>
      )}

      {others.length > 0 && <div className="banner info cl-queue">{othersSummary(others)}</div>}

      {me.stockCheckDue && online && (
        <FridayStockPrompt
          siteId={open?.siteId ?? null}
          popUp={!flow && !stockSnoozed && (clockedIn || justPunched)}
          onLater={() => setStockSnoozed(true)}
          onAnswered={() => void reload()}
        />
      )}

      {flow && (
        <PunchFlow
          kind={flow.kind}
          mode={flow.mode}
          presetToken={flow.token}
          me={me}
          onClose={(changed) => {
            setFlow(null);
            if (changed) {
              setStockSnoozed(false);
              setJustPunched(true);
              void reload();
            }
          }}
        />
      )}
    </div>
  );
}
